import { Colors } from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { XIcon } from '../PrepareMeetingIcons';
import { HandoutSectionTitle } from './HandoutSectionTitle';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { HandoutCompanyValueData } from '@/types/review/ReviewDiscussion';

import {
    useGetReviewDiscussion,
    useUpdateReviewDiscussion,
} from '@/api/review-services/review-discussion';
import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { mapHandoutCompanyValueToCardProps } from '@/pages/ManagerAssessment/utils/assessmentResultUtils';
import { mergeHandoutAnnotations } from '@/pages/ManagerAssessment/utils/handoutAnnotations';
import { CompanyValueCard } from '@/pages/shared/ManagerAssessment/CompanyValueCard';

interface HandoutCompanyValuesProps {
    companyValues?: HandoutCompanyValueData[];
    reviewInstanceId?: string;
    proficiencyLevels?: ProficiencyLevel[];
}

export const HandoutCompanyValues = ({
    companyValues = [],
    reviewInstanceId,
    proficiencyLevels = [],
}: HandoutCompanyValuesProps) => {
    const { t, i18n } = useTranslation();
    const [values, setValues] = useState(companyValues);
    const { data: reviewDiscussion } = useGetReviewDiscussion(reviewInstanceId || '');
    const { mutate: saveDiscussion } = useUpdateReviewDiscussion(reviewInstanceId || '');

    useEffect(() => {
        setValues(companyValues);
    }, [companyValues]);

    const persistValues = useMemo(
        () => (nextValues: HandoutCompanyValueData[]) => {
            if (!reviewInstanceId) {
                return;
            }
            saveDiscussion({
                handoutAnnotations: mergeHandoutAnnotations(reviewDiscussion?.handoutAnnotations, {
                    companyValues: nextValues,
                }),
            });
        },
        [reviewInstanceId, reviewDiscussion?.handoutAnnotations, saveDiscussion],
    );

    if (values.length === 0) {
        return null;
    }

    return (
        <div
            className={'mb-4 break-inside-avoid py-3 sm:mb-8 sm:py-6'}
            style={{ pageBreakInside: 'avoid' }}
        >
            <div
                className={'group mb-[8px] flex break-inside-avoid items-start justify-between'}
                style={{ pageBreakInside: 'avoid' }}
            >
                <div>
                    <HandoutSectionTitle className={'sm:whitespace-nowrap'}>
                        {t('postReviewPrepareMeeting.companyValues.title')}
                    </HandoutSectionTitle>
                    <p className={'mt-[8px] text-sm text-gray-500'}>
                        {t('postReviewPrepareMeeting.companyValues.subtitle')}
                    </p>
                </div>
                <button
                    className={
                        'rounded-lg bg-red-50 p-2 opacity-0 transition-opacity group-hover:opacity-100'
                    }
                    type={'button'}
                >
                    <span className={'sr-only'}>
                        {t('postReviewPrepareMeeting.companyValues.deleteSection')}
                    </span>
                    <XIcon className={'h-4 w-4 text-red-500'} />
                </button>
            </div>
            <div
                className={
                    'grid break-inside-avoid grid-cols-1 gap-x-[12px] gap-y-5 md:grid-cols-2'
                }
                style={{ pageBreakInside: 'avoid' }}
            >
                {values.map((cv) => {
                    const cardProps = mapHandoutCompanyValueToCardProps(cv, proficiencyLevels);

                    const saveUpdated = (nextValues: HandoutCompanyValueData[]) => {
                        setValues(nextValues);
                        persistValues(nextValues);
                    };
                    const iconEntry = cardProps.levelIcon
                        ? getIconEntry(cardProps.levelIcon)
                        : undefined;

                    const BadgeIcon = () => {
                        return (
                            <ProficiencyIcon
                                iconEntry={iconEntry}
                                color={Colors.WHITE}
                            />
                        );
                    };
                    return (
                        <div
                            key={cv.id}
                            className={'break-inside-avoid'}
                            style={{ pageBreakInside: 'avoid' }}
                            data-pdf-block={'true'}
                        >
                            <CompanyValueCard
                                {...cardProps}
                                isEditable
                                badgeIcon={<BadgeIcon />}
                                onSave={(updatedDescription, updatedMetricValue) => {
                                    const updatedValues = values.map((item) =>
                                        item.id === cv.id
                                            ? {
                                                  ...item,
                                                  description: {
                                                      ...(item.description ?? {}),
                                                      [i18n.language]: updatedDescription,
                                                  },
                                                  assessedScore: updatedMetricValue,
                                              }
                                            : item,
                                    );
                                    saveUpdated(updatedValues);
                                }}
                                onDelete={() => {
                                    const updatedValues = values.filter(
                                        (item) => item.id !== cv.id,
                                    );
                                    saveUpdated(updatedValues);
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
